import Autocomplete from "@mui/material/Autocomplete";
import React, { Fragment, useMemo } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { Checkbox } from "@/Components/v2/atoms/Checkbox";
import { Divider } from "@/Components/v2/atoms/Divider";
import { InputAdornment } from "@/Components/v2/atoms/InputAdornment";
import { Stack } from "@/Components/v2/atoms/Stack";
import { TextField } from "@/Components/v2/atoms/TextField";
import { Typography } from "@/Components/v2/atoms/Typography";

import { SELECT_ALL } from "./shared";
import { MultiSelectProps, OptionV2 } from "./types";

const DefaultWidth = 240;

export const MultiSelectV2 = ({
  label,
  id,
  options,
  value,
  placeholder = `Select a ${label}`,
  onChange,
  width = DefaultWidth,
  disabled = false,
  disableClearable = false,
  allOptionsLabel,
  disableAllOption = false,
}: MultiSelectProps) => {
  const selectedOptions = options.filter((opt) => value.includes(opt.value));
  const isAllSelected = options.length > 0 && value.length === options.length;

  const optionsWithSelectAll = useMemo(
    () =>
      disableAllOption
        ? options
        : [{ label: allOptionsLabel || `All`, value: SELECT_ALL }, ...options],
    [disableAllOption, options, allOptionsLabel, label]
  );

  const handleSelectAll = () => {
    onChange(isAllSelected ? [] : options.map((opt) => opt.value));
  };

  const handleChange = (_: unknown, selected: OptionV2[]) => {
    if (!selected) {
      onChange([]);
    } else {
      onChange(selected.map((opt) => opt.value));
    }
  };

  const getSelectedLabel = () => {
    if (!value.length) return undefined;
    if (isAllSelected) return "All";
    return value.length === 1
      ? options.find((opt) => opt.value === value[0])?.label
      : `${value.length} selected`;
  };

  const selectedLabel = getSelectedLabel();

  return (
    <Autocomplete
      multiple
      id={id}
      options={optionsWithSelectAll}
      value={selectedOptions}
      onChange={handleChange}
      disabled={disabled}
      disableClearable={disableClearable}
      disableCloseOnSelect
      getOptionLabel={(option: OptionV2) => option.label}
      sx={{
        width,
        "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
          paddingLeft: "12px",
          borderRadius: "4px",
        },
      }}
      isOptionEqualToValue={(option: OptionV2, value: OptionV2) => option.value === value.value}
      renderOption={(
        props: React.HTMLAttributes<HTMLLIElement>,
        option: OptionV2,
        { selected }: { selected: boolean }
      ) => {
        if (option.value === SELECT_ALL) {
          return (
            <Fragment key={SELECT_ALL}>
              <li {...props} onClick={handleSelectAll}>
                <Box onClick={handleSelectAll} sx={{ cursor: "pointer" }}>
                  <Checkbox
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    style={{ marginRight: 8 }}
                  />
                  {option.label}
                </Box>
              </li>
              <Divider sx={{ marginY: "0.5rem" }} />
            </Fragment>
          );
        }
        return (
          <li {...props} key={option.value}>
            <Checkbox style={{ marginRight: 8 }} checked={selected} />
            {option.label}
          </li>
        );
      }}
      style={{ width }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={value.length === 0 ? placeholder : ""}
          id={id}
          sx={{
            "& .MuiInputBase-root": {
              padding: "4px 8px",
              borderRadius: "8px",
            },
            "& .MuiInputLabel-root": {
              display: "none",
            },
          }}
          InputLabelProps={{}}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Stack direction="row" alignItems="center">
                  {label && (
                    <InputAdornment position="start">
                      <Typography fontSize="16px" fontWeight="600" color="text.secondary">
                        {label}:
                      </Typography>
                      {selectedLabel && (
                        <Typography component="span" fontSize="16px" color="text.secondary" pl={1}>
                          {selectedLabel}
                        </Typography>
                      )}
                    </InputAdornment>
                  )}
                </Stack>
              </>
            ),
          }}
          size="small"
        />
      )}
    />
  );
};
